import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Edit3, BookOpen, Zap, Save, Eye, EyeOff, GripVertical, X, Check, Loader } from 'lucide-react';
import Header from '../../components/ui/Header';
import Button from '../../components/ui/Button';
import { useAuth } from '../../contexts/AuthContext';
import { egwPlanService } from '../../services/egwPlanService';

// ── Drag-and-drop page chip ───────────────────────────────────────────────────

const PageChip = ({ page, onRemove, draggable, onDragStart }) => (
  <div
    draggable={draggable}
    onDragStart={onDragStart}
    className="inline-flex items-center gap-1 px-2 py-1 bg-primary/15 border border-primary/30 rounded-md text-xs text-primary cursor-grab active:cursor-grabbing select-none"
  >
    {draggable && <GripVertical size={10} className="text-primary/50" />}
    <span className="font-mono font-semibold">p.{page?.page_number}</span>
    <span className="text-primary/70 max-w-[80px] truncate">{page?.preview || ''}</span>
    {onRemove && (
      <button onClick={() => onRemove(page)} className="ml-1 text-primary/50 hover:text-destructive transition-colors">
        <X size={10} />
      </button>
    )}
  </div>
);

// ── Day card ──────────────────────────────────────────────────────────────────

const DayCard = ({ day, onDrop, onRemovePage, onRemoveDay, onUpdateTitle }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState(day?.title || '');

  const handleDragOver = (e) => {
    e?.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = (e) => {
    e?.preventDefault();
    setIsDragOver(false);
    const pageData = JSON.parse(e?.dataTransfer?.getData('application/json'));
    onDrop(day, pageData);
  };

  const handleTitleSave = () => {
    onUpdateTitle(day?.id, titleValue);
    setEditingTitle(false);
  };

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`bg-card border rounded-xl p-4 transition-all duration-150 ${isDragOver ? 'border-primary bg-primary/5 shadow-lg' : 'border-border'}`}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            Day {day?.day_number}
          </span>
          {editingTitle ? (
            <div className="flex items-center gap-1">
              <input
                value={titleValue}
                onChange={(e) => setTitleValue(e?.target?.value)}
                className="text-xs bg-input border border-border rounded px-2 py-0.5 text-foreground w-32"
                placeholder="Day title..."
                autoFocus
                onKeyDown={(e) => e?.key === 'Enter' && handleTitleSave()}
              />
              <button onClick={handleTitleSave} className="text-success hover:text-success/80">
                <Check size={12} />
              </button>
              <button onClick={() => setEditingTitle(false)} className="text-muted-foreground hover:text-foreground">
                <X size={12} />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setEditingTitle(true)}
              className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
            >
              {day?.title || <span className="italic">Add title</span>}
              <Edit3 size={10} />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1">
          <span className="text-xs text-muted-foreground">{day?.pages?.length || 0} pages</span>
          {(!day?.pages?.length) && (
            <button
              onClick={() => onRemoveDay(day)}
              className="text-muted-foreground hover:text-destructive transition-colors ml-1"
              title="Remove empty day"
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      </div>
      <div className="min-h-[40px] flex flex-wrap gap-1.5">
        {isDragOver && !day?.pages?.length && (
          <div className="w-full text-center text-xs text-primary/60 py-2 border border-dashed border-primary/30 rounded-lg">
            Drop pages here
          </div>
        )}
        {(day?.pages || [])?.map((page) => (
          <PageChip
            key={page?.id || page?.page_number}
            page={page}
            draggable
            onDragStart={(e) => {
              e?.dataTransfer?.setData('application/json', JSON.stringify({ ...page, sourceDayId: day?.id }));
            }}
            onRemove={onRemovePage}
          />
        ))}
        {!day?.pages?.length && !isDragOver && (
          <span className="text-xs text-muted-foreground/50 italic">Empty — drag pages here</span>
        )}
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const EGWPlanManagement = () => {
  const { user } = useAuth();

  // Plans list
  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);

  // Selected plan
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [editingPlan, setEditingPlan] = useState(null);
  const [savingPlan, setSavingPlan] = useState(false);

  // Book codes
  const [bookCodes, setBookCodes] = useState([]);

  // Day builder
  const [days, setDays] = useState([]);
  const [unassignedPages, setUnassignedPages] = useState([]);
  const [allBookPages, setAllBookPages] = useState([]);
  const [daysLoading, setDaysLoading] = useState(false);
  const [autoGenerating, setAutoGenerating] = useState(false);

  // Notifications
  const [notification, setNotification] = useState(null);

  const showNotification = (msg, type = 'success') => {
    setNotification({ msg, type });
    setTimeout(() => setNotification(null), 3000);
  };

  // Load plans on mount
  useEffect(() => {
    loadPlans();
    loadBookCodes();
  }, []);

  const loadPlans = async () => {
    setPlansLoading(true);
    const { data, error } = await egwPlanService?.getAllPlans();
    if (!error) setPlans(data || []);
    setPlansLoading(false);
  };

  const loadBookCodes = async () => {
    const { data } = await egwPlanService?.getAvailableBookCodes();
    setBookCodes(data || []);
  };

  const loadDayBuilder = useCallback(async (plan) => {
    if (!plan?.id || !plan?.book_code) return;
    setDaysLoading(true);

    const [daysResult, pagesResult] = await Promise.all([
      egwPlanService?.getPlanDays(plan?.id),
      egwPlanService?.getBookPages(plan?.book_code),
    ]);

    const daysData = daysResult?.data;
    const pagesData = pagesResult?.data;

    setAllBookPages(pagesData || []);

    // Build days with their pages
    const builtDays = (daysData || [])?.map(day => ({
      ...day,
      pages: (day?.egw_plan_day_pages || [])?.sort((a, b) => a?.display_order - b?.display_order)?.map(dp => {
          const pg = (pagesData || [])?.find(p => p?.page_number === dp?.page_number);
          return { ...dp, preview: pg?.preview || '' };
        }),
    }));

    setDays(builtDays);

    // Compute unassigned pages
    const assignedPageNums = new Set(builtDays.flatMap(d => d.pages.map(p => p.page_number)));
    setUnassignedPages((pagesData || [])?.filter(p => !assignedPageNums?.has(p?.page_number)));

    setDaysLoading(false);
  }, []);

  const handleSelectPlan = (plan) => {
    setSelectedPlan(plan);
    setEditingPlan({ ...plan });
    loadDayBuilder(plan);
  };

  const handleNewPlan = () => {
    const blank = {
      id: null,
      title: '',
      description: '',
      book_code: bookCodes?.[0] || 'DA',
      book_title: '',
      series: '',
      cover_image_url: '',
      default_pace: 3,
      is_active: true,
      is_published: false,
    };
    setSelectedPlan(blank);
    setEditingPlan(blank);
    setDays([]);
    setUnassignedPages([]);
  };

  const handleSavePlan = async () => {
    if (!editingPlan?.title || !editingPlan?.book_code) {
      showNotification('Title and Book Code are required', 'error');
      return;
    }
    setSavingPlan(true);
    try {
      if (editingPlan?.id) {
        const { data, error } = await egwPlanService?.updatePlan(editingPlan?.id, editingPlan);
        if (error) throw error;
        setSelectedPlan(data);
        setEditingPlan(data);
        showNotification('Plan saved!');
      } else {
        const { data, error } = await egwPlanService?.createPlan(editingPlan, user?.id);
        if (error) throw error;
        setSelectedPlan(data);
        setEditingPlan(data);
        showNotification('Plan created!');
        await loadDayBuilder(data);
      }
      await loadPlans();
    } catch (err) {
      showNotification(err?.message, 'error');
    } finally {
      setSavingPlan(false);
    }
  };

  const handleAutoGenerate = async () => {
    if (!selectedPlan?.id) {
      showNotification('Save the plan first', 'error');
      return;
    }
    setAutoGenerating(true);
    try {
      const { error, totalDays, totalPages } = await egwPlanService?.autoGenerateDays(
        selectedPlan?.id,
        editingPlan?.book_code,
        editingPlan?.default_pace
      );
      if (error) throw error;
      showNotification(`Generated ${totalDays} days across ${totalPages} pages!`);
      // Refresh plan data so total_days / total_pages update in the list
      await loadPlans();
      // Reload the day builder with the freshly-generated days
      await loadDayBuilder(selectedPlan);
    } catch (err) {
      showNotification(err?.message, 'error');
    } finally {
      setAutoGenerating(false);
    }
  };

  const handleTogglePublished = async (plan) => {
    const { error } = await egwPlanService?.togglePublished(plan?.id, !plan?.is_published);
    if (!error) {
      showNotification(`Plan ${!plan?.is_published ? 'published' : 'unpublished'}`);
      await loadPlans();
      if (selectedPlan?.id === plan?.id) {
        setSelectedPlan(prev => ({ ...prev, is_published: !plan?.is_published }));
        setEditingPlan(prev => ({ ...prev, is_published: !plan?.is_published }));
      }
    }
  };

  const handleDeletePlan = async (plan) => {
    if (!window.confirm(`Delete plan "${plan?.title}"? This cannot be undone.`)) return;
    const { error } = await egwPlanService?.deletePlan(plan?.id);
    if (!error) {
      showNotification('Plan deleted');
      if (selectedPlan?.id === plan?.id) {
        setSelectedPlan(null);
        setEditingPlan(null);
        setDays([]);
      }
      await loadPlans();
    }
  };

  // Day builder handlers
  const handleDropOnDay = async (targetDay, pageData) => {
    const sourceDayId = pageData?.sourceDayId;

    if (sourceDayId === targetDay?.id) return; // Same day, no-op

    if (sourceDayId) {
      // Moving from one day to another
      await egwPlanService?.movePageToDay(pageData?.id, targetDay?.id, selectedPlan?.id, targetDay?.pages?.length || 0);
    } else {
      // Moving from unassigned
      await egwPlanService?.assignPageToDay(
        targetDay?.id,
        selectedPlan?.id,
        pageData?.page_number,
        pageData?.content_sequence_id,
        targetDay?.pages?.length || 0
      );
    }

    await loadDayBuilder(selectedPlan);
  };

  const handleRemovePageFromDay = async (page) => {
    await egwPlanService?.removePageFromDay(page?.id);
    await loadDayBuilder(selectedPlan);
  };

  const handleAddDay = async () => {
    const nextDayNum = (days?.length > 0 ? Math.max(...days?.map(d => d?.day_number)) : 0) + 1;
    const { error } = await egwPlanService?.addDay(selectedPlan?.id, nextDayNum);
    if (!error) await loadDayBuilder(selectedPlan);
  };

  const handleRemoveDay = async (day) => {
    if (day?.pages?.length > 0) {
      showNotification('Remove all pages from this day first', 'error');
      return;
    }
    const { error } = await egwPlanService?.removeDay(day?.id);
    if (!error) await loadDayBuilder(selectedPlan);
  };

  const handleUpdateDayTitle = async (dayId, title) => {
    await egwPlanService?.updateDayTitle(dayId, title);
    setDays(prev => prev?.map(d => d?.id === dayId ? { ...d, title } : d));
  };

  const handleUnassignedDragStart = (e, page) => {
    e?.dataTransfer?.setData('application/json', JSON.stringify(page));
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      {/* Notification */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg text-sm font-medium transition-all ${
          notification?.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-success text-success-foreground'
        }`}>
          {notification?.msg}
        </div>
      )}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to="/admin-dashboard" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-3">
            <ArrowLeft className="w-4 h-4" />
            Admin Dashboard
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-1">EGW Plan Management</h1>
              <p className="text-muted-foreground">Create and manage Ellen G. White reading plans</p>
            </div>
            <Button onClick={handleNewPlan} className="flex items-center gap-2">
              <Plus className="w-4 h-4" />
              New Plan
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Plans List */}
          <div className="xl:col-span-1">
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="p-4 border-b border-border">
                <h2 className="font-semibold text-foreground">All Plans</h2>
              </div>
              {plansLoading ? (
                <div className="p-8 text-center">
                  <Loader className="w-6 h-6 animate-spin text-primary mx-auto" />
                </div>
              ) : plans?.length === 0 ? (
                <div className="p-8 text-center text-muted-foreground text-sm">
                  No plans yet. Click "New Plan" to create one.
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {plans?.map(plan => (
                    <div
                      key={plan?.id}
                      onClick={() => handleSelectPlan(plan)}
                      className={`p-4 cursor-pointer hover:bg-muted/30 transition-colors ${selectedPlan?.id === plan?.id ? 'bg-primary/5 border-l-2 border-primary' : ''}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground text-sm truncate">{plan?.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {plan?.book_code} · {plan?.total_days} days · {plan?.total_pages} pages
                          </p>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={(e) => { e?.stopPropagation(); handleTogglePublished(plan); }}
                            className={`p-1 rounded transition-colors ${plan?.is_published ? 'text-success hover:text-success/80' : 'text-muted-foreground hover:text-foreground'}`}
                            title={plan?.is_published ? 'Unpublish' : 'Publish'}
                          >
                            {plan?.is_published ? <Eye size={14} /> : <EyeOff size={14} />}
                          </button>
                          <button
                            onClick={(e) => { e?.stopPropagation(); handleDeletePlan(plan); }}
                            className="p-1 rounded text-muted-foreground hover:text-destructive transition-colors"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 mt-2">
                        <span className={`text-xs px-2 py-0.5 rounded-full ${plan?.is_published ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                          {plan?.is_published ? 'Published' : 'Draft'}
                        </span>
                        {plan?.series && (
                          <span className="text-xs text-muted-foreground">{plan?.series}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Plan Editor + Day Builder */}
          <div className="xl:col-span-2 space-y-6">
            {selectedPlan !== null ? (
              <>
                {/* Plan Editor */}
                <div className="bg-card border border-border rounded-xl p-6">
                  <h2 className="font-semibold text-foreground mb-4">
                    {editingPlan?.id ? 'Edit Plan' : 'New Plan'}
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Title *</label>
                      <input
                        value={editingPlan?.title || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, title: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="e.g. Desire of Ages — 3 Pages/Day"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Book Code *</label>
                      <select
                        value={editingPlan?.book_code || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, book_code: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      >
                        {bookCodes?.map(code => (
                          <option key={code} value={code}>{code}</option>
                        ))}
                        {bookCodes?.length === 0 && <option value="DA">DA</option>}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Book Title</label>
                      <input
                        value={editingPlan?.book_title || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, book_title: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="e.g. Desire of Ages"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Series</label>
                      <input
                        value={editingPlan?.series || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, series: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="e.g. Conflict of Ages"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Default Pace (pages/day)</label>
                      <input
                        type="number"
                        min={1}
                        max={10}
                        value={editingPlan?.default_pace || 3}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, default_pace: parseInt(e?.target?.value) || 3 }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-1">Cover Image URL</label>
                      <input
                        value={editingPlan?.cover_image_url || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, cover_image_url: e?.target?.value }))}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                        placeholder="https://..."
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                      <textarea
                        value={editingPlan?.description || ''}
                        onChange={(e) => setEditingPlan(prev => ({ ...prev, description: e?.target?.value }))}
                        rows={2}
                        className="w-full px-3 py-2 bg-input border border-border rounded-lg text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                        placeholder="Brief description of this reading plan..."
                      />
                    </div>
                  </div>

                  <div className="flex items-center gap-3 mt-4">
                    <Button onClick={handleSavePlan} disabled={savingPlan} className="flex items-center gap-2">
                      {savingPlan ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                      Save Plan
                    </Button>
                    {editingPlan?.id && (
                      <Button
                        variant="outline"
                        onClick={handleAutoGenerate}
                        disabled={autoGenerating}
                        className="flex items-center gap-2"
                      >
                        {autoGenerating ? <Loader className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                        Auto-Generate Days
                      </Button>
                    )}
                  </div>
                </div>

                {/* Day Builder */}
                {editingPlan?.id && (
                  <div className="bg-card border border-border rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h2 className="font-semibold text-foreground">Day Builder</h2>
                      <Button variant="outline" size="sm" onClick={handleAddDay} className="flex items-center gap-1">
                        <Plus className="w-3 h-3" />
                        Add Day
                      </Button>
                    </div>

                    {daysLoading ? (
                      <div className="text-center py-8">
                        <Loader className="w-6 h-6 animate-spin text-primary mx-auto" />
                        <p className="text-sm text-muted-foreground mt-2">Loading day builder...</p>
                      </div>
                    ) : (
                      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                        {/* Unassigned Pages */}
                        <div className="lg:col-span-1">
                          <div className="bg-muted/30 border border-border rounded-xl p-4 h-full">
                            <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                              <BookOpen size={14} className="text-primary" />
                              Unassigned Pages
                              <span className="text-xs text-muted-foreground ml-auto">{unassignedPages?.length}</span>
                            </h3>
                            <div className="space-y-1.5 max-h-[500px] overflow-y-auto">
                              {unassignedPages?.length === 0 ? (
                                <p className="text-xs text-muted-foreground italic">All pages assigned</p>
                              ) : (
                                unassignedPages?.map(page => (
                                  <div
                                    key={page?.page_number}
                                    draggable
                                    onDragStart={(e) => handleUnassignedDragStart(e, page)}
                                    className="flex items-center gap-2 p-2 bg-card border border-border rounded-lg cursor-grab hover:border-primary/40 transition-colors"
                                  >
                                    <GripVertical size={12} className="text-muted-foreground shrink-0" />
                                    <span className="text-xs font-mono font-semibold text-primary shrink-0">p.{page?.page_number}</span>
                                    <span className="text-xs text-muted-foreground truncate">{page?.preview}</span>
                                  </div>
                                ))
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Day Cards */}
                        <div className="lg:col-span-2">
                          {days?.length === 0 ? (
                            <div className="text-center py-12 text-muted-foreground text-sm">
                              <BookOpen size={32} className="mx-auto mb-2 text-muted-foreground/40" />
                              No days yet. Use "Auto-Generate Days" or "Add Day" to start.
                            </div>
                          ) : (
                            <div className="space-y-3 max-h-[600px] overflow-y-auto pr-1">
                              {days?.map(day => (
                                <DayCard
                                  key={day?.id}
                                  day={day}
                                  onDrop={handleDropOnDay}
                                  onRemovePage={handleRemovePageFromDay}
                                  onRemoveDay={handleRemoveDay}
                                  onUpdateTitle={handleUpdateDayTitle}
                                />
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </>
            ) : (
              <div className="bg-card border border-border rounded-xl p-12 text-center">
                <BookOpen size={48} className="mx-auto mb-4 text-muted-foreground/40" />
                <h3 className="text-lg font-semibold text-foreground mb-2">Select or Create a Plan</h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Choose a plan from the list or create a new one to get started.
                </p>
                <Button onClick={handleNewPlan} className="flex items-center gap-2 mx-auto">
                  <Plus className="w-4 h-4" />
                  New Plan
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EGWPlanManagement;
